import { useState, useRef, useEffect, useCallback } from 'react';
import MobileHeader from '@/components/base/MobileHeader';

export interface PhotoDetailImage {
  id?: string;
  url: string;
  label?: string;
  sectionName?: string;
}

interface PhotoDetailViewerProps {
  images: PhotoDetailImage[];
  initialIndex: number;
  title: string;
  subtitle?: string;
  onClose: () => void;
}

export default function PhotoDetailViewer({
  images,
  initialIndex,
  title,
  subtitle,
  onClose,
}: PhotoDetailViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const total = images.length;

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const ref = thumbnailRefs.current[currentIndex];
    if (ref) {
      ref.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [currentIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
  }, [total]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
  }, [total]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        goToPrev();
        return;
      }
      if (e.key === 'ArrowRight') {
        goToNext();
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrev, goToNext]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const currentImage = images[currentIndex];

  return (
    <div className="fixed inset-0 z-50 bg-static-black flex flex-col">
      <MobileHeader
        onBack={onClose}
        title={title}
        rightContent={<>{currentIndex + 1} / {total}</>}
      />

      {subtitle && (
        <div className="text-center py-2 bg-static-black">
          <p className="text-caption text-[#9A9188]">{subtitle}</p>
        </div>
      )}

      <div className="flex-1 relative flex items-center justify-center bg-static-black overflow-hidden">
        <img
          src={currentImage.url}
          alt={currentImage.label || `사진 ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />

        <button
          onClick={goToPrev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-static-black/30 flex items-center justify-center text-static-white cursor-pointer hover:bg-static-black/45 transition-colors"
          aria-label="이전 사진"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>

        <button
          onClick={goToNext}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-static-black/30 flex items-center justify-center text-static-white cursor-pointer hover:bg-static-black/45 transition-colors"
          aria-label="다음 사진"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        {currentImage.label && (
          <div className="absolute bottom-20 left-4 right-4 flex justify-center">
            <span className="px-3 py-1 rounded-full bg-static-black/55 text-static-white text-caption">
              {currentImage.label}
            </span>
          </div>
        )}
      </div>

      <div className="bg-static-black border-t border-static-white/10 py-3">
        <div className="flex gap-2 overflow-x-auto px-4" style={{ scrollbarWidth: 'none' }}>
          {images.map((img, i) => (
            <button
              key={i}
              ref={(el) => { thumbnailRefs.current[i] = el; }}
              onClick={() => setCurrentIndex(i)}
              className={`flex-shrink-0 w-[56px] h-[56px] rounded-lg overflow-hidden cursor-pointer transition-all ${
                i === currentIndex
                  ? 'ring-2 ring-[#B4563F] opacity-100 scale-105'
                  : 'opacity-55 hover:opacity-80'
              }`}
            >
              <img
                src={img.url}
                alt={img.label || `썸네일 ${i + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
